'use client';

import { Check, Link2 } from 'lucide-react';
import { useParams, usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { copyToClipboard } from '@/utils/clipboard';

/**
 * Displays owner/repo breadcrumb in header when viewing a specific wiki page.
 * Only renders on /wikis/[owner]/[repo] routes.
 */
export function WikiBreadcrumb() {
  const params = useParams();
  const pathname = usePathname();

  const isWikiPage = pathname.includes('/wikis/') && params?.owner && params?.repo;
  const owner = params?.owner as string;
  const repo = params?.repo as string;

  if (!isWikiPage) {
    return null;
  }

  return (
    // Phones have no room beside the header actions; small screens show the
    // repo alone, truncated, and the owner returns at md.
    <div className="hidden min-w-0 items-center gap-2 sm:flex">
      <span className="text-lg text-muted-foreground">/</span>
      <span className="truncate text-lg font-medium text-zinc-300">
        <span className="hidden md:inline">{`${owner}/`}</span>
        {repo}
      </span>
    </div>
  );
}

/**
 * Share button that copies the current URL to clipboard.
 * Only renders on /wikis/[owner]/[repo] routes.
 */
export function WikiShareButton() {
  const [copied, setCopied] = useState(false);
  const params = useParams();
  const pathname = usePathname();

  const isWikiPage = pathname.includes('/wikis/') && params?.owner && params?.repo;

  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (copied) {
      timeoutId = setTimeout(() => setCopied(false), 2000);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [copied]);

  const handleShare = async () => {
    if (typeof window !== 'undefined') {
      const success = await copyToClipboard(window.location.href, {
        successMessage: 'Link copied to clipboard',
        errorMessage: 'Failed to copy link',
      });

      if (success) {
        setCopied(true);
      }
    }
  };

  if (!isWikiPage) {
    return null;
  }

  return (
    <Button size="sm" variant="ghost" onClick={handleShare} className="transition-all">
      {copied ? (
        <>
          <Check className="mr-2 h-4 w-4 text-green-500" />
          Link Copied
        </>
      ) : (
        <>
          <Link2 className="mr-2 h-4 w-4" />
          Share
        </>
      )}
    </Button>
  );
}
